import React, { useContext, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';

import useIsComDomain from '@src/hooks/useIsComDomain';
import { DeviceContext, deviceType } from '@providers';

import ProductListTemplate from '@src/componentsNew/ProductListTemplate/ProductListTemplate';
import DesktopProductListPageSkeleton from '@src/componentsNew/Skeleton/desktop/page/DesktopProductListPageSkeleton';
import MobileProductListPageSkeleton from '@src/componentsNew/Skeleton/mobile/page/MobileProductListPageSkeleton';

import { DisplayMenuType, OrderDirection, ProductOrderField, StockAvailability } from '__generated__/globalTypes';
import { DISPLAY_MENUS } from '@src/pages/ProductList/Display/queries';
import {
  displayMenus,
  displayMenus_display_menus,
  displayMenus_display_menus_children,
  displayMenus_display_menus_brand,
} from '@src/pages/ProductList/Display/__generated__/displayMenus';
import { productsVariables } from '@components/ProductPage/ProductList/__generated__/products';

import { getGraphqlIdFromDBId } from '@src/core/utils';
import { DESKTOP_PRODUCT_LIST_REQUEST_COUNT, MOBILE_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';

export const DisplayPage = ({ match }) => {
  const { isMobile } = useContext<deviceType>(DeviceContext);
  const isComDomain = useIsComDomain();

  const displayGraphqlId = getGraphqlIdFromDBId(match.params.id, 'Display');

  const defaultVariables = {
    stockAvailability: StockAvailability.IN_STOCK,
    sortBy: {
      field: ProductOrderField.POPULARITY,
      direction: OrderDirection.DESC,
    },
    first: isMobile ? MOBILE_PRODUCT_LIST_REQUEST_COUNT : DESKTOP_PRODUCT_LIST_REQUEST_COUNT,
    ...(isComDomain && { isMgs: true }),
  };

  const [selectedLevel0Menu, setSelectedLevel0Menu] = useState<displayMenus_display_menus>(null);
  const [selectedLevel1Menu, setSelectedLevel1Menu] = useState<displayMenus_display_menus_children>(null);
  const [brandInfo, setBrandInfo] = useState<displayMenus_display_menus_brand>(null);

  const [variables, setVariables] = useState<productsVariables>(null);
  const [productTotalCount, setProductTotalCount] = useState<number>(0);

  const { data, loading } = useQuery<displayMenus>(DISPLAY_MENUS, {
    variables: {
      id: displayGraphqlId,
      isMobile,
    },
    onCompleted: (data) => {
      const firstSubmenu = data?.display?.menus[0];
      setSelectedLevel0Menu(firstSubmenu);

      if (!firstSubmenu?.hasAllSubmenu) {
        setSelectedLevel1Menu(firstSubmenu?.children[0]);
      }

      const secondArgument = getCreateVariablesSecondArgument(firstSubmenu);
      const newVariables = createVariables(firstSubmenu, secondArgument);

      setVariables(newVariables);
    },
  });

  const getCreateVariablesSecondArgument = (level0Submenu: displayMenus_display_menus) => {
    if (level0Submenu?.hasAllSubmenu || level0Submenu?.children?.length === 0) {
      return null;
    }

    return level0Submenu?.children[0];
  };

  const createVariables = (
    level0Menu: displayMenus_display_menus | null,
    level1Menu: displayMenus_display_menus_children | null,
  ): productsVariables => {
    let newVariables: productsVariables = { ...defaultVariables };

    if (level0Menu) {
      switch (level0Menu.type) {
        case DisplayMenuType.CATEGORY_1:
        case DisplayMenuType.CATEGORY_2:
          newVariables.categories = [getGraphqlIdFromDBId(level0Menu.categoryId?.toString(), 'Category')];
          break;
        case DisplayMenuType.BRAND:
          newVariables.brands = [getGraphqlIdFromDBId(level0Menu.brandId?.toString(), 'Brand')];
          break;
        case DisplayMenuType.COLLECTION:
          newVariables.collections = [getGraphqlIdFromDBId(level0Menu.collectionId?.toString(), 'Collection')];
          break;
        case DisplayMenuType.TAG:
          newVariables.tags = [getGraphqlIdFromDBId(level0Menu.tagId?.toString(), 'Tag')];
          break;
      }
    }

    if (level1Menu) {
      switch (level1Menu.type) {
        case DisplayMenuType.CATEGORY_1:
        case DisplayMenuType.CATEGORY_2:
          // If level0 was also a category, combine them
          if (newVariables.categories?.length) {
            newVariables.categories = [
              ...newVariables?.categories,
              getGraphqlIdFromDBId(level1Menu.categoryId?.toString(), 'Category'),
            ];
          } else {
            newVariables.categories = [getGraphqlIdFromDBId(level1Menu.categoryId?.toString(), 'Category')];
          }
          break;
        case DisplayMenuType.BRAND:
          if (newVariables.brands?.length) {
            newVariables.brands = [
              ...newVariables.brands,
              getGraphqlIdFromDBId(level1Menu.brandId?.toString(), 'Brand'),
            ];
          } else {
            newVariables.brands = [getGraphqlIdFromDBId(level1Menu.brandId?.toString(), 'Brand')];
          }
          break;
        case DisplayMenuType.COLLECTION:
          if (newVariables.collections?.length) {
            newVariables.collections = [
              ...newVariables.collections,
              getGraphqlIdFromDBId(level1Menu.collectionId?.toString(), 'Collection'),
            ];
          } else {
            newVariables.collections = [getGraphqlIdFromDBId(level1Menu.collectionId?.toString(), 'Collection')];
          }
          break;
        case DisplayMenuType.TAG:
          if (newVariables.tags?.length) {
            newVariables.tags = [...newVariables.tags, getGraphqlIdFromDBId(level1Menu.tagId?.toString(), 'Tag')];
          } else {
            newVariables.tags = [getGraphqlIdFromDBId(level1Menu.tagId?.toString(), 'Tag')];
          }
          break;
      }
    }

    return newVariables;
  };

  const handleClickLevel0SubMenuButton = (e: React.MouseEvent, menu: displayMenus_display_menus) => {
    e.persist();
    const secondArgument = getCreateVariablesSecondArgument(menu);

    if (menu?.sortOrder !== 0 && menu?.type === 'BRAND' && menu?.brand?.images?.length > 0) {
      setBrandInfo(menu?.brand);
    } else {
      setBrandInfo(null);
    }

    setSelectedLevel0Menu(menu);
    setSelectedLevel1Menu(secondArgument);

    const newVariables = createVariables(menu, secondArgument);
    setVariables(newVariables);
  };

  const handleClickLevel1SubMenuButton = (e: React.MouseEvent, menu: displayMenus_display_menus_children) => {
    e.persist();

    setSelectedLevel1Menu(menu);

    const newVariables = createVariables(selectedLevel0Menu, menu);
    setVariables(newVariables);
  };

  if (loading || !variables) {
    return isMobile ? (
      <MobileProductListPageSkeleton type="brand" />
    ) : (
      <DesktopProductListPageSkeleton type="brand" productCount={DESKTOP_PRODUCT_LIST_REQUEST_COUNT} />
    );
  }

  return (
    <ProductListTemplate
      pageTitle={data?.display?.name}
      images={
        selectedLevel0Menu?.sortOrder === 0 ? (isMobile ? data?.display?.imageMobile : data?.display?.image) : null
      }
      brandInfo={brandInfo}
      level0SubMenu={data?.display?.menus}
      level1SubMenu={selectedLevel0Menu?.children}
      showLevel1SubmenuAllButton={selectedLevel0Menu?.hasAllSubmenu}
      selectedLevel0SubMenu={selectedLevel0Menu}
      selectedLevel1SubMenu={selectedLevel1Menu}
      handleClickLevel0SubMenu={handleClickLevel0SubMenuButton}
      handleClickLevel1SubMenu={handleClickLevel1SubMenuButton}
      productTotalCount={productTotalCount}
      setProductTotalCount={setProductTotalCount}
      variables={variables}
      setVariables={setVariables}
    />
  );
};

export default DisplayPage;
